import { ArrowRight, Globe2, LayoutGrid, MonitorSmartphone, Target } from "lucide-react";
import { LaptopMockup } from "@/components/iv/LaptopMockup";
import { PresetPills } from "@/components/iv/PresetPills";
import { ConceptStrip } from "@/components/iv/ConceptStrip";
import { useApp } from "@/context/AppContext";

const POINTS = [
  { icon: Target, title: "20-parameter stress-test", text: "12 universal + 8 industry-overlay criteria, each scored 0–10 with cited evidence and deterministic, verifiable maths." },
  { icon: Globe2, title: "Local, Country & Global lenses", text: "See demand, competition and regulation for your pin-code, your country and the world — side by side." },
  { icon: MonitorSmartphone, title: "Free 30-minute test website", text: "A mobile-ready preview site with brand, hero copy and features so you can collect real sign-ups today." },
];

export const Hero = ({ onStage }) => {
  const { activeReport } = useApp();
  return (
    <section className="grid items-center gap-10 py-10 lg:grid-cols-[1.05fr_1fr] lg:gap-14 lg:py-16" aria-labelledby="hero-title" data-testid="hero-section">
      <div className="iv-rise min-w-0">
        <span className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-500/[0.06] px-3 py-1 text-[11px] font-semibold text-cyan-300">
          <LayoutGrid className="h-3.5 w-3.5" aria-hidden="true" /> Evidence-first startup validation
        </span>
        <h1 id="hero-title" className="mt-5 text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="hero-title">
          From Idea to Investable Business — <span className="iv-gradient-text">With Evidence.</span>
        </h1>
        <p className="mt-5 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base">
          IdeaValidator360 runs your startup idea through a 20-parameter stress-test, reads it through Local, Country and Global lenses,
          and hands you a 4-tile bento teardown, customer discovery scripts and a free test website — before you spend a rupee or a dollar.
        </p>
        <ul className="mt-7 space-y-4">
          {POINTS.map(({ icon: Icon, title, text }) => (
            <li key={title} className="flex gap-3">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-white/10 bg-white/[0.04]"><Icon className="h-4 w-4 text-cyan-300" aria-hidden="true" /></span>
              <span className="min-w-0"><span className="block text-sm font-semibold text-white">{title}</span><span className="block text-xs leading-relaxed text-slate-400 sm:text-sm">{text}</span></span>
            </li>
          ))}
        </ul>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <button type="button" onClick={() => onStage(2)} className="iv-cta inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-white" data-testid="hero-validate-cta">
            Validate My Idea — Free <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </button>
          <button type="button" onClick={() => onStage(3)} className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-semibold text-slate-200 transition-colors duration-200 hover:border-cyan-400/50 hover:text-white" data-testid="hero-scorecard-cta">
            See 20-Point Bento Scorecard
          </button>
        </div>
      </div>
      <div className="iv-rise iv-delay-2 min-w-0 space-y-4">
        <LaptopMockup report={activeReport} />
        <PresetPills />
        <ConceptStrip report={activeReport} />
      </div>
    </section>
  );
};
